import React, { Suspense, useCallback } from "react";
import { useNavigate } from "react-router-dom";

// The renderer (react-markdown and its plugins, ~100 KB) is loaded only when a page's body has to be
// rendered in the browser: a page reached by in-app navigation.
const MarketingMarkdownRenderer = React.lazy(() => import("./MarketingMarkdownRenderer"));

/**
 * A marketing page's Markdown body. A page served as static HTML comes with its body already rendered
 * (`renderedHtml`, produced by MarketingMarkdownRenderer at build time and carried in the page), and that
 * is what is shown: rendering nothing anew keeps React's adoption of the static page cheap, and the
 * renderer stays out of the page's JavaScript. In-app links in that HTML are turned into router
 * navigations here, as the renderer's RouterLink would have done. Without `renderedHtml` (in-app
 * navigation to another page) the body is rendered from the Markdown.
 */
export const MarketingMarkdown = React.memo(function MarketingMarkdown({
  markdown,
  renderedHtml,
  className,
}: {
  markdown: string;
  renderedHtml?: string | null;
  className: string;
}) {
  const navigate = useNavigate();
  const navigateForInternalLinkClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const anchor = (event.target as HTMLElement).closest("a");
      if (!anchor || anchor.target === "_blank" || anchor.hasAttribute("download")) return;
      const href = anchor.getAttribute("href");
      if (!href || !href.startsWith("/")) return;
      event.preventDefault();
      navigate(href);
    },
    [navigate]
  );

  if (renderedHtml) {
    // Our own content, rendered by our own renderer at build time.
    return (
      <div
        data-marketing-rendered-body
        className={className}
        dangerouslySetInnerHTML={{ __html: renderedHtml }}
        onClick={navigateForInternalLinkClick}
      />
    );
  }
  return (
    <div className={className}>
      <Suspense fallback={null}>
        <MarketingMarkdownRenderer markdown={markdown} />
      </Suspense>
    </div>
  );
});
